import type { NavigationItem as NavigationItemType } from '@content';
import { useLocale } from '@hooks/use-locale';
import { cn } from '@lib/theme';
import { Link } from '@tanstack/react-router';
import { NavigationIcon } from '@ui/NavigationIcon';
import { motion, type Variants } from 'framer-motion';
import { forwardRef } from 'react';

interface NavigationItemProps {
  item: NavigationItemType;
  onClick: () => void;
  itemVariants: Variants;
}

const NavigationItem = forwardRef<HTMLAnchorElement, NavigationItemProps>(({ item, onClick, itemVariants }, ref) => {
  const { basePathname, locale, localizeHref } = useLocale();
  const isTamil = locale === 'ta';
  const itemHref = localizeHref(item.href);
  const isActive = basePathname === item.href;

  return (
    <motion.div variants={itemVariants}>
      <Link
        ref={ref}
        to={itemHref}
        onClick={onClick}
        className={cn('group/item relative flex items-center text-left', isTamil ? 'gap-3' : 'gap-4')}
        aria-current={isActive ? 'page' : undefined}
      >
        <NavigationIcon variant={item.icon} />
        <span className="flex flex-col">
          <span className="relative inline-block">
            <span
              className={cn(
                'text-foreground font-bold',
                isTamil ? 'text-xl leading-snug md:text-3xl' : 'text-3xl md:text-5xl'
              )}
            >
              {item.name}
            </span>
            <span
              className={cn(
                'text-brand-peacock pointer-events-none absolute inset-0 font-bold transition-[clip-path] duration-400 ease-out motion-reduce:transition-none',
                isActive
                  ? '[clip-path:inset(0_0_0_0)]'
                  : '[clip-path:inset(0_100%_0_0)] group-focus-within/item:[clip-path:inset(0_0_0_0)] group-hover/item:[clip-path:inset(0_0_0_0)]',
                isTamil ? 'text-xl leading-snug md:text-3xl' : 'text-3xl md:text-5xl'
              )}
              aria-hidden="true"
            >
              {item.name}
            </span>
          </span>
          {item.description ? (
            <span
              className={cn(
                'text-foreground/70 mt-1.5',
                isTamil ? 'max-w-md text-xs leading-relaxed md:text-sm' : 'max-w-lg text-sm md:text-base'
              )}
            >
              {item.description}
            </span>
          ) : null}
        </span>
      </Link>
    </motion.div>
  );
});

NavigationItem.displayName = 'NavigationItem';

export default NavigationItem;
